import { Component, OnInit, ChangeDetectionStrategy, inject, input, output, signal } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { ButtonComponent } from '../../../common/button/button.component';
import { BillingService } from '../../../../services/billing.service';
import {
  AvailableProduct,
  SubscriptionItem,
  SubscriptionItemLocation,
  LocationPriceConfig
} from '../../../../model/subscription-billing';
import { LocationPriceConfigComponent } from '../shared/location-price-config/location-price-config.component';
import { SearchableSelectorComponent, SelectorOption } from '../../../common/searchable-selector/searchable-selector.component';

export interface AddSubscriptionItemInputData {
  businessId: string;
  availableProducts: AvailableProduct[];
  billingCycle: 'MONTHLY' | 'YEARLY';
  locations: SubscriptionItemLocation[];
  subscriptionItems: SubscriptionItem[];
  multiLocationEnabled: boolean;
}

export interface AddSubscriptionItemResult {
  success: boolean;
  product?: AvailableProduct;
  locationIds: string[] | null;
  unitPrice: number;
  error?: string;
}

@Component({
  selector: 'app-add-subscription-item',
  imports: [
    FormsModule,
    ButtonComponent,
    LocationPriceConfigComponent,
    SearchableSelectorComponent
],
  templateUrl: './add-subscription-item.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './add-subscription-item.component.scss'
})
export class AddSubscriptionItemComponent implements OnInit {
  private billingService = inject(BillingService);

  readonly inputData = input<AddSubscriptionItemInputData | null>(null);

  readonly itemAdded = output<AddSubscriptionItemResult>();
  readonly cancelled = output<void>();

  availableProducts: AvailableProduct[] = [];
  locations: SubscriptionItemLocation[] = [];
  subscriptionItems: SubscriptionItem[] = [];
  billingCycle: 'MONTHLY' | 'YEARLY' = 'MONTHLY';
  businessId = '';
  selectedProductId = '';
  multiLocationEnabled = false;

  applyToAllLocations = true;
  selectedLocationIds = new Set<string>();

  useDefaultPrice = true;
  customPrice: number | null = null;
  isLoading = signal<boolean>(false);

  ngOnInit(): void {
    const inputData = this.inputData();
    if (!inputData) return;
    this.businessId = inputData.businessId;
    this.billingCycle = inputData.billingCycle || 'MONTHLY';
    this.locations = inputData.locations ?? [];
    this.subscriptionItems = inputData.subscriptionItems ?? [];
    this.multiLocationEnabled = inputData.multiLocationEnabled === true;
    this.availableProducts = this.filterCatalog(inputData.availableProducts ?? []);
    // Without multi-location the only valid target is business-wide.
    if (!this.multiLocationEnabled) {
      this.applyToAllLocations = true;
      this.selectedLocationIds.clear();
    }
  }

  /**
   * The featureId a real item belongs to. The subscription-item DTO carries no
   * featureId (the service maps it to '') — only featureCode — so resolve it
   * through the catalog. Mock items DO carry a featureId; prefer it when set.
   */
  private featureIdOf(item: SubscriptionItem): string | undefined {
    const sp = item.subscriptionProduct;
    if (!sp) return undefined;
    if (sp.featureId) return sp.featureId;
    const code = (sp.featureCode ?? '').toLowerCase();
    return code
      ? (this.inputData()?.availableProducts ?? []).find(p => p.featureCode === code)?.id
      : undefined;
  }

  // Drop products already on the subscription business-wide (a duplicate
  // would silently succeed at the API but pollute the items table). For
  // location-scoped dedup we let the user pick locations, then narrow at
  // submit time.
  private filterCatalog(catalog: AvailableProduct[]): AvailableProduct[] {
    const businessWideFeatureIds = new Set(
      this.subscriptionItems
        .filter(it => !it.location)
        .map(it => this.featureIdOf(it))
    );
    return catalog.filter(p => {
      if (businessWideFeatureIds.has(p.id)) return false;
      const spId = this.spIdFor(p, this.billingCycle);
      return !!spId;
    });
  }

  private spIdFor(p: AvailableProduct, cycle: 'MONTHLY' | 'YEARLY'): string | undefined {
    return cycle === 'MONTHLY' ? p.monthlySubscriptionProductId : p.yearlySubscriptionProductId;
  }

  // Locations that already have this product. Used to gate the Specific list.
  private occupiedLocationIdsFor(featureId: string): Set<string> {
    return new Set(
      this.subscriptionItems
        .filter(it => this.featureIdOf(it) === featureId && it.location?.id)
        .map(it => it.location!.id)
    );
  }

  get hasLocations(): boolean {
    return this.locations.length > 0;
  }

  get selectedProduct(): AvailableProduct | undefined {
    return this.availableProducts.find(p => p.id === this.selectedProductId);
  }

  get productOptions(): SelectorOption[] {
    return this.availableProducts.map(product => ({
      id: product.id,
      label: product.name,
      description: product.description,
      icon: product.icon,
      data: product
    }));
  }

  get defaultPrice(): number {
    if (!this.selectedProduct) return 0;
    return this.billingCycle === 'MONTHLY'
      ? this.selectedProduct.monthlyPrice
      : this.selectedProduct.yearlyPrice;
  }

  get effectiveUnitPrice(): number {
    if (this.useDefaultPrice || this.customPrice === null) {
      return this.defaultPrice;
    }
    return this.customPrice;
  }

  // Business-wide = one item, billed once. Specific = N items, billed N×.
  get computedItemCount(): number {
    return this.applyToAllLocations ? 1 : Math.max(1, this.selectedLocationIds.size);
  }

  get totalPrice(): number {
    return this.effectiveUnitPrice * this.computedItemCount;
  }

  get billingCycleLabel(): string {
    return this.billingCycle === 'MONTHLY' ? 'mo' : 'yr';
  }

  get selectedLocationsCount(): number {
    return this.selectedLocationIds.size;
  }

  // Show the Select-Specific UI only when the business actually owns the
  // multi-location feature.
  get canSelectSpecificLocations(): boolean {
    return this.multiLocationEnabled && this.hasLocations;
  }

  onProductSelect(option: SelectorOption | SelectorOption[] | null): void {
    if (Array.isArray(option)) {
      this.selectedProductId = option[0]?.id ?? '';
    } else {
      this.selectedProductId = option?.id ?? '';
    }
    this.customPrice = null;
    this.useDefaultPrice = true;
    this.applyToAllLocations = true;
    this.selectedLocationIds.clear();
  }

  onConfigChanged(config: LocationPriceConfig): void {
    this.applyToAllLocations = config.applyToAllLocations;
    this.selectedLocationIds = new Set(config.selectedLocationIds);
    this.useDefaultPrice = config.useDefaultPrice;
    this.customPrice = config.customPrice;
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  }

  onSubmit(): void {
    const product = this.selectedProduct;
    if (!product) return;
    if (!this.applyToAllLocations && this.selectedLocationIds.size === 0) return;

    const spId = this.spIdFor(product, this.billingCycle);
    if (!spId) {
      this.itemAdded.emit({
        success: false,
        product,
        locationIds: null,
        unitPrice: this.effectiveUnitPrice,
        error: `No ${this.billingCycle.toLowerCase()} pricing configured for ${product.name}.`
      });
      return;
    }

    // Skip locations that already have this product (avoid creating duplicate
    // rows that the backend would currently accept on the service path).
    let locationIds: string[] | null;
    if (this.applyToAllLocations) {
      locationIds = null;
    } else {
      const occupied = this.occupiedLocationIdsFor(product.id);
      locationIds = Array.from(this.selectedLocationIds).filter(id => !occupied.has(id));
      if (locationIds.length === 0) {
        this.itemAdded.emit({
          success: false,
          product,
          locationIds: null,
          unitPrice: this.effectiveUnitPrice,
          error: `${product.name} is already on every selected location.`
        });
        return;
      }
    }

    const overrideCents = this.useDefaultPrice || this.customPrice === null
      ? null
      : Math.round(this.customPrice * 100);

    this.isLoading.set(true);
    this.billingService.addSubscriptionItem(this.businessId, spId, locationIds, overrideCents).subscribe({
      next: () => {
        this.isLoading.set(false);
        this.itemAdded.emit({
          success: true,
          product,
          locationIds,
          unitPrice: this.effectiveUnitPrice
        });
      },
      error: (err) => {
        this.isLoading.set(false);
        this.itemAdded.emit({
          success: false,
          product,
          locationIds,
          unitPrice: this.effectiveUnitPrice,
          error: err?.message ?? 'Failed to add subscription item.'
        });
      }
    });
  }

  onCancel(): void {
    this.cancelled.emit();
  }
}
